import React from 'react';
import { DASH_CONTENT_CARD, DASH_CARD_TITLE } from '../../../shared/components/dashboard';

/* ── Shared types ── */

export interface Modelo {
  codigo: string;
  nombre: string;
  coste: string;
}

export interface Proveedor {
  id: string;
  nombre: string;
  tipo: string;
  hasApiKey: boolean;
  keySource: 'bd' | 'env' | null;
  activo: boolean;
  modelos: Modelo[];
  showKey: boolean;
  editKey: boolean;
  keyDraft: string;
  testResult: 'ok' | 'error' | null;
  testMsg: string;
  testing: boolean;
  // Campos de configuración avanzada (prompt 371)
  baseUrl?: string | null;
  modeloPredeterminado?: string | null;
  prioridad?: number;
  compatibleOpenAI?: boolean;
  soportaStreaming?: boolean;
  maxContextTokens?: number;
  tpmLimit?: number | null;
  rpmLimit?: number | null;
}

export interface AssistMode {
  modo: string;
  modelo: string;
  temp: string;
  tokens: string;
}

export interface GlossaryTerm {
  term: string;
  definition: string;
}

export interface DashboardData {
  interacciones: number;
  tokensUsados: number;
  tokensLimite: number;
  costeTotalEstimado: number;
  latenciaMedia: number;
  tasaExito: number;
  similitudMedia: number;
  chunksMediaConsulta: number;
  docsIndexados: number;
  chunksTotal: number;
  dimensiones: number;
  proveedores: Array<{
    nombre: string;
    modelo: string;
    rol: string;
    llamadas: number;
    tokens: number;
    latencia: number;
    coste: number;
    estado: string;
    creditos: number | null;
  }>;
  consumoPorModo: Array<{ modo: string; tokens: number }>;
  actividadDiaria: Array<{ dia: string; interacciones: number; tokensMedio: number }>;
  heatmap: number[][];
}

export const DASHBOARD_FALLBACK: DashboardData = {
  interacciones: 0,
  tokensUsados: 0,
  tokensLimite: 1000000,
  costeTotalEstimado: 0,
  latenciaMedia: 0,
  tasaExito: 0,
  similitudMedia: 0,
  chunksMediaConsulta: 0,
  docsIndexados: 0,
  chunksTotal: 0,
  dimensiones: 0,
  proveedores: [],
  consumoPorModo: [],
  actividadDiaria: [],
  heatmap: [],
};

export interface ChartMonth {
  mes: string;
  chat: number;
  gen: number;
  corr: number;
  aud: number;
  trad: number;
}

export interface NewProviderForm {
  nombre: string;
  tipo: string;
  apiKey: string;
  baseUrl?: string;
  modeloPredeterminado?: string;
  prioridad?: number;
}

/** Tipos de proveedor que tienen SDK propio (no usan OpenAI-compatible) */
export const SDK_NATIVE_PROVIDER_TYPES = new Set([
  'gemini',
  'google',
  'claude',
  'anthropic',
  'groq',
]);

export interface ModelOption {
  value: string;
  label: string;
}

/* ── Shared styles ── */

export const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(0,0,0,0.54)',
  marginBottom: 4,
};

export const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '8px 14px',
  border: '1px solid #d5d5d5',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
};

export const TH_STYLE: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#fff',
  backgroundColor: '#0E243E',
  textAlign: 'left',
};

export const CELL_INPUT: React.CSSProperties = {
  padding: '3px 5px',
  border: '1px solid #D9D9D9',
  borderRadius: 4,
  fontSize: 10,
  fontFamily: 'inherit',
};

export const SECTION_CARD: React.CSSProperties = {
  ...DASH_CONTENT_CARD,
  flex: 'unset',
  marginBottom: 20,
};

export const SECTION_TITLE: React.CSSProperties = {
  ...DASH_CARD_TITLE,
  fontSize: 14,
  marginBottom: 16,
  textTransform: 'none',
  letterSpacing: 0,
};

export const MODEL_PILL: React.CSSProperties = {
  background: '#E8EFF9',
  color: '#2F5496',
  borderRadius: 12,
  padding: '2px 8px',
  fontSize: 10,
  fontWeight: 600,
};

export const INITIAL_GLOSSARY: GlossaryTerm[] = [
  { term: 'VALOR\u00B7IA', definition: 'Ecosistema de gobernanza corporativa' },
  { term: 'V\u00B7IANet', definition: 'Plataforma SaaS de gobernanza' },
  { term: 'PRL', definition: 'Prevencion de Riesgos Laborales' },
];

export const PROVIDER_NAMES: Record<string, string> = {
  claude: 'Anthropic',
  anthropic: 'Anthropic',
  groq: 'Groq',
  gemini: 'Google AI',
  google: 'Google AI',
  openai: 'OpenAI',
  huggingface: 'HuggingFace',
  mistral: 'Mistral AI',
  openrouter: 'OpenRouter',
  deepseek: 'DeepSeek',
  cerebras: 'Cerebras',
  nvidia: 'NVIDIA NIM',
  ollama: 'Ollama',
};
